import type { BalanceExportFormat, ExportFileResult } from '@features/balanceLedger/types/balanceExport'
import type { BalanceSummaryQueryParams } from '@features/balanceLedger/types/balanceSummaryQueryParams'
import { buildBalanceStatementQueryParams } from '@features/balanceLedger/utils/buildBalanceStatementQueryParams'
import { buildBalanceSummaryQueryParams } from '@features/balanceLedger/utils/buildBalanceSummaryQueryParams'
import { fetchBalanceExportFile } from '@features/balanceLedger/utils/fetchBalanceExportFile'

import { Metable } from '../../types/metable'
import { getRequest } from '../../utils'

export interface CreditorBalanceMoney {
  value: number
  currency: string
}

export interface CreditorBalanceContact {
  contactId: string
  contactName: string
  contactNumber: string
  auxiliaryAccountNo: string | null
  balance: CreditorBalanceMoney
  comparisonBalance?: CreditorBalanceMoney
  variance?: CreditorBalanceMoney
  variancePercent?: number | null
}

export interface CreditorBalancePagination {
  page: number
  pageSize: number
  totalEntries: number
  totalPages: number
}

export interface CreditorBalanceTotals {
  balance: CreditorBalanceMoney
  comparisonBalance?: CreditorBalanceMoney
}

export interface CreditorBalanceSummaryResponse extends Metable {
  contacts: CreditorBalanceContact[]
  pagination: CreditorBalancePagination
  totals?: CreditorBalanceTotals
}

export interface FetchCreditorBalanceSummaryParams extends BalanceSummaryQueryParams {
  organizationId: string
}

export type FetchCreditorBalanceSummaryExportParams = Omit<FetchCreditorBalanceSummaryParams, 'page' | 'pageSize'> & {
  format: BalanceExportFormat
}

const getCreditorBalanceSummaryExportFallbackFileName = (format: BalanceExportFormat): string =>
  `Supplier-balance.${format}`

export const fetchCreditorBalanceSummary = ({
  organizationId,
  period,
  page,
  pageSize,
  sortBy,
  sortDirection,
  search,
  contactIds,
  balanceDirection,
}: FetchCreditorBalanceSummaryParams): Promise<CreditorBalanceSummaryResponse> => {
  const queryParams = buildBalanceSummaryQueryParams({
    period,
    page,
    pageSize,
    sortBy,
    sortDirection,
    search,
    contactIds,
    balanceDirection,
  })

  return getRequest(`/v2/organizations/${organizationId}/creditorBalanceSummary?${queryParams}`)
}

export const fetchCreditorBalanceSummaryExport = async ({
  organizationId,
  format,
  period,
  sortBy,
  sortDirection,
  search,
  contactIds,
  balanceDirection,
}: FetchCreditorBalanceSummaryExportParams): Promise<ExportFileResult> => {
  const queryParams = buildBalanceSummaryQueryParams({
    period,
    sortBy,
    sortDirection,
    search,
    contactIds,
    balanceDirection,
  })
  const querySuffix = queryParams ? `?${queryParams}` : ''
  const path = `/v2/organizations/${organizationId}/creditorBalanceSummary.${format}${querySuffix}`

  return fetchBalanceExportFile(path, format, getCreditorBalanceSummaryExportFallbackFileName(format))
}

export interface CreditorBalanceStatementContact {
  contactId: string
  contactName: string
  contactNumber: string
  auxiliaryAccountNo: string | null
}

export interface CreditorBalanceStatementEntry {
  entryDate: string
  originatorType: string
  originatorRef: string
  /* Null when the entry has no booking behind it (e.g. unpaid cash-accounting bill) */
  transactionId?: string | null
  description: string | null
  debit: CreditorBalanceMoney
  credit: CreditorBalanceMoney
  journal: string | null
  runningBalance: CreditorBalanceMoney
  isOpeningBalance: boolean
  bill?: {
    documentNumber: string | null
    documentCount: number
    voucherBalance: CreditorBalanceMoney | null
  }
}

export interface CreditorBalanceStatementResponse {
  data: {
    contact: CreditorBalanceStatementContact
    openingBalance: CreditorBalanceMoney
    periodMovement: CreditorBalanceMoney
    closingBalance: CreditorBalanceMoney
    entries: CreditorBalanceStatementEntry[]
    pagination: CreditorBalancePagination
  }
}

export interface FetchCreditorBalanceStatementParams {
  organizationId: string
  contactId: string
  period?: string
  search?: string
  page?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: 'ASC' | 'DESC'
}

export type FetchCreditorBalanceStatementExportParams = Omit<
  FetchCreditorBalanceStatementParams,
  'page' | 'pageSize' | 'sortBy' | 'sortDirection'
> & {
  format: BalanceExportFormat
}

const getCreditorBalanceStatementExportFallbackFileName = (format: BalanceExportFormat): string => `Statement.${format}`

export const fetchCreditorBalanceStatement = ({
  organizationId,
  contactId,
  period,
  search,
  page,
  pageSize,
  sortBy,
  sortDirection,
}: FetchCreditorBalanceStatementParams): Promise<CreditorBalanceStatementResponse> => {
  const queryParams = buildBalanceStatementQueryParams({
    period,
    search,
    page,
    pageSize,
    sortBy,
    sortDirection,
  })

  return getRequest(`/v2/organizations/${organizationId}/creditorBalanceStatement/${contactId}?${queryParams}`)
}

export const fetchCreditorBalanceStatementExport = async ({
  organizationId,
  contactId,
  format,
  period,
  search,
}: FetchCreditorBalanceStatementExportParams): Promise<ExportFileResult> => {
  const queryParams = buildBalanceStatementQueryParams({
    period,
    search,
  })
  const querySuffix = queryParams ? `?${queryParams}` : ''
  const path = `/v2/organizations/${organizationId}/creditorBalanceStatement/${contactId}.${format}${querySuffix}`

  return fetchBalanceExportFile(path, format, getCreditorBalanceStatementExportFallbackFileName(format))
}
